import React from 'react';
import { cn } from '../../lib/utils';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    suffix?: string;
    error?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({ className, label, suffix, error, type = 'text', ...props }, ref) => {
        return (
            <div className="space-y-1">
                {label && (
                    <label className="text-sm font-medium text-surface-600">{label}</label>
                )}
                <div className="relative flex items-center">
                    <input
                        ref={ref}
                        type={type}
                        className={cn(
                            'w-full h-10 px-3 rounded-lg border bg-white text-surface-800',
                            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
                            'transition-all duration-200',
                            'placeholder:text-surface-400',
                            error ? 'border-red-500' : 'border-surface-300',
                            suffix && 'pr-12',
                            className
                        )}
                        {...props}
                    />
                    {suffix && (
                        <span className="absolute right-3 text-sm text-surface-500">{suffix}</span>
                    )}
                </div>
                {error && <p className="text-xs text-red-500">{error}</p>}
            </div>
        );
    }
);

Input.displayName = 'Input';

interface NumberInputProps extends Omit<InputProps, 'value' | 'onChange'> {
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
}

export const NumberInput: React.FC<NumberInputProps> = ({
    value,
    onChange,
    min,
    max,
    step = 1,
    ...props
}) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = parseFloat(e.target.value);
        if (!isNaN(val)) {
            if (min !== undefined && val < min) return onChange(min);
            if (max !== undefined && val > max) return onChange(max);
            onChange(val);
        }
    };

    return (
        <Input
            type="number"
            value={value}
            onChange={handleChange}
            min={min}
            max={max}
            step={step}
            {...props}
        />
    );
};
